import React, { useEffect, useMemo, useState } from "react";
import { InputNumberFormat, format, unformat } from "@react-input/number-format";
import { useLocation, useNavigate } from "react-router-dom";
import { formatMoney } from "utilities/formatMoney";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import { useDispatch, useSelector } from "react-redux";
import CurrencyToggle from "component/currencyToggle";
import currencyPairCheck from "utilities/currencyPairCheck";
import {
    resetBankTransfer,
    setBankAmount,
    setBankCurrency,
    setBankProvider,
    setBankAmount as setTxnAmount,
} from "../../../../redux/slices/bankTransferSlice";
import { ArrowRight2, Location, Map1, Timer1 } from "iconsax-reactjs";
import { ACHIcon, RTPIcon, WireIcon } from "asset/icons";
import { fetchTransferFees } from "../../../../api/fetchers";
import {
    resetUsdTransfer,
    setUsdAll,
} from "../../../../redux/slices/usdTransferSlice";
import { resetTransfer } from "../../../../redux/slices/tradevuTransferSlice";
import { resetCryptoTransfer } from "../../../../redux/slices/cryptoTransferSlice";
import { resetCryptoFiatTransfer } from "../../../../redux/slices/cryptoFiatTransferSlice";
import RecipientTypeModal from "component/pay/RecipientTypeModal";
import SelectBankProviderModal from "component/wallet/SelectBankProviderModal";

const SameCurrencyOtherUsers = () => {
    const dispatch = useDispatch();
    const [amount, setAmount] = useState("");
    const location = useLocation();
    const { business, currency, wallets } = useSelector((state) => state);
    const isUSD = String(currency).toUpperCase() === "USD";
    const isKES = String(currency).toUpperCase() === "KES";
    const [showRecipientLocationModal, setShowRecipientLocationModal] = useState(false);
    const [showTransferMethodModal, setShowTransferMethodModal] = useState(false);
    const [recipientLocation, setRecipientLocation] = useState("");
    const [provider, setProviderState] = useState(null);
    const [showProviderModal, setShowProviderModal] = useState(false);

    const LOCALE = "en";

    const handleAmountChange = (e) => {
        const v = e.target.value || "";
        const cleaned = v
            .replace(/,/g, "")
            .replace(/[^\d.]/g, "")
            .replace(/^(\d*\.\d*).*$/, "$1");

        setAmount(cleaned);
        dispatch(setBankAmount(cleaned));

        if (isUSD) dispatch(setUsdAll({ amount: cleaned, currency: "USD" }));
    };

    const formatted = amount ? format(amount, { locales: LOCALE, maximumFractionDigits: 2 }) : "";

    const handleAmountClear = () => {
        setAmount("");
        dispatch(setBankAmount(""));
        if (isUSD) dispatch(setUsdAll({ amount: "" }));
    };

    const [paymentRail, setPaymentRail] = useState("");

    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;
    const navigate = useNavigate();

    const [minAmount, setMinAmount] = useState(0);
    const [maxAmount, setMaxAmount] = useState(0);
    const [dailyLimit, setDailyLimit] = useState(0);
    const [monthlyLimit, setMonthlyLimit] = useState(0);
    const [currentDaily, setCurrentDaily] = useState(0);
    const [currentMonthly, setCurrentMonthly] = useState(0);
    const [feeLoading, setFeeLoading] = useState(false);
    const [feeReady, setFeeReady] = useState(false);
    const [rateKey, setRateKey] = useState("");

    const [fee, setFee] = useState(0);

    const currentWallet = wallets?.find((wallet) => wallet.currency === currency);
    const providers = useMemo(
        () => (Array.isArray(currentWallet?.providers) ? currentWallet.providers : []),
        [currentWallet]
    );
    const hasMultipleProviders = providers.length > 1 && !isKES;
    const selectedProviderBalance = useMemo(() => {
        if (!hasMultipleProviders || !provider) return null;
        const value = Number(provider?.balance);
        return Number.isFinite(value) ? value : null;
    }, [hasMultipleProviders, provider]);
    const balance =
        selectedProviderBalance !== null ? selectedProviderBalance : currentWallet?.balance || 0;
    const numericAmount = Number.parseFloat(amount || "0") || 0;
    const usdPricingInputsReady = Boolean(
        isUSD && numericAmount > 0 && recipientLocation && paymentRail
    );
    const needsProvider = hasMultipleProviders && !provider;

    useEffect(() => {
        sessionStorage.setItem("previousTransactionRoute", location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        dispatch(setBankCurrency(currency));
    }, [currency, dispatch]);

    useEffect(() => {
        setProviderState(null);
    }, [currency]);

    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
    }, [dispatch]);

    useEffect(() => {
        if (!isUSD || !recipientLocation) return;
        const accountType = recipientLocation === "US" ? "us" : "iban";
        dispatch(setUsdAll({ accountType }));
        sessionStorage.setItem("usdRecipientLocation", recipientLocation);
        sessionStorage.setItem("recipientType", recipientLocation);
    }, [recipientLocation, dispatch, isUSD]);

    useEffect(() => {
        if (!isUSD || !paymentRail) return;
        dispatch(setUsdAll({ paymentRail }));
        sessionStorage.setItem("paymentRail", paymentRail);
        sessionStorage.setItem("usdPaymentMethod", paymentRail);
    }, [paymentRail, dispatch, isUSD]);
    useEffect(() => {
        if (!businessId || numericAmount <= 0 || (isUSD && !usdPricingInputsReady)) {
            setFeeLoading(false);
            setFeeReady(false);
            setFee(0);
            setMinAmount(0);
            setMaxAmount(0);
            setRateKey("");
            return;
        }
        let cancelled = false;

        (async () => {
            try {
                setFeeLoading(true);
                setFeeReady(false);
                setMinAmount(0);
                setMaxAmount(0);
                const res = await fetchTransferFees({
                    businessId,
                    params: {
                        currency: String(currency).toUpperCase(),
                        currencyTo: String(currency).toUpperCase(),
                        amount: numericAmount,
                        ...(isUSD ? { paymentRail } : {}),
                    },
                });
                const payload = res?.data ?? res ?? {};
                const data = payload?.data ?? payload;

                const transferFee = Number(data.transactionFee) || 0;
                const rateKey = String(data.rateKey || "");
                const apiMin = Number(data.minimumTransferAmount) || 0;
                const apiMax = Number(data.maximumTransferAmount) || 0;
                const dLimit = Number(data.dailyTransactionLimit) || 0;
                const mLimit = Number(data.monthlyTransactionLimit) || 0;
                const dUsed = Number(data.currentDailyTransfer) || 0;
                const mUsed = Number(data.currentMonthlyTransfer) || 0;

                if (!cancelled) {
                    setMinAmount(Number.isFinite(apiMin) ? apiMin : 0);
                    setMaxAmount(Number.isFinite(apiMax) ? apiMax : 0);
                    setFee(Number.isFinite(transferFee) ? transferFee : 0);
                    setDailyLimit(dLimit);
                    setMonthlyLimit(mLimit);
                    setCurrentDaily(dUsed);
                    setCurrentMonthly(mUsed);
                    setRateKey(rateKey);
                    setFeeReady(true);
                }
            } catch {
                if (!cancelled) {
                    setMinAmount(0);
                    setMaxAmount(0);
                    setFeeReady(false);
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        businessId,
        currency,
        numericAmount,
        paymentRail,
        recipientLocation,
        isUSD,
        usdPricingInputsReady,
    ]);

    const transferMethodLabel = {
        ach: "ACH",
        rtp: "RTP",
        wire: "Fedwire Transfer",
    }[paymentRail] || "Select payment method";

    const recipientLocationLabel =
        recipientLocation === "US"
            ? "Recipient in the US"
            : recipientLocation === "NON_US"
              ? "Recipient outside the US"
              : "Select recipient location";

    const deliveryByRail = {
        ach: "Arrives in 1–5 days",
        rtp: "Arrives in minutes",
        wire: "Arrives on the same day",
    }[paymentRail];

    const total = numericAmount + (Number(fee) || 0);

    // remaining capacities
    const dailyRemaining = Math.max(0, (dailyLimit || 0) - (currentDaily || 0));
    const monthlyRemaining = Math.max(0, (monthlyLimit || 0) - (currentMonthly || 0));
    const exceedDaily = dailyLimit > 0 && numericAmount > dailyRemaining;
    const exceedMonthly = monthlyLimit > 0 && numericAmount > monthlyRemaining;
    const WARN_PCT = 0.9;
    const nearDaily =
        dailyLimit > 0 &&
        !exceedDaily &&
        (currentDaily + numericAmount) / dailyLimit >= WARN_PCT &&
        numericAmount > 0;
    const nearMonthly =
        monthlyLimit > 0 &&
        !exceedMonthly &&
        (currentMonthly + numericAmount) / monthlyLimit >= WARN_PCT &&
        numericAmount > 0;

    const tooLow = feeReady && minAmount > 0 && numericAmount < minAmount;
    const tooHigh = maxAmount > 0 && numericAmount > maxAmount;
    const insufficient = total > balance;

    const blockReason = useMemo(() => {
        if (!numericAmount || numericAmount <= 0) return "";

        if (tooLow) {
            return `Minimum transfer amount is ${currencyPairCheck(currency)}${formatMoney(minAmount)}.`;
        }

        if (tooHigh) {
            return `Maximum transfer amount is ${currencyPairCheck(currency)}${formatMoney(maxAmount)}.`;
        }

        if (exceedDaily) {
            return `This exceeds your daily limit. Remaining today: ${currencyPairCheck(currency)}${formatMoney(dailyRemaining)}.`;
        }

        if (exceedMonthly) {
            return `This exceeds your monthly limit. Remaining this month: ${currencyPairCheck(currency)}${formatMoney(monthlyRemaining)}.`;
        }

        if (insufficient) {
            return `Insufficient balance. Available: ${currencyPairCheck(currency)}${formatMoney(balance)}.`;
        }

        return "";
    }, [
        numericAmount,
        tooLow,
        tooHigh,
        exceedDaily,
        exceedMonthly,
        insufficient,
        currency,
        minAmount,
        maxAmount,
        dailyRemaining,
        monthlyRemaining,
        balance,
    ]);

    const hasUsdOptions = !isUSD || Boolean(recipientLocation && paymentRail);
    const isAmountValid =
        numericAmount > 0 &&
        !tooLow &&
        !tooHigh &&
        !exceedDaily &&
        !exceedMonthly &&
        !insufficient &&
        feeReady &&
        !feeLoading &&
        hasUsdOptions &&
        !needsProvider;
    const showError = numericAmount > 0 && total > balance;

    const onContinue = () => {
        if (isUSD) {
            dispatch(
                setUsdAll({
                    amount,
                    currency: "USD",
                    accountType: recipientLocation === "US" ? "us" : "iban",
                    paymentRail,
                    rateKey,
                })
            );
        } else {
            dispatch(setBankAmount(amount));
            dispatch(setBankCurrency(currency));
        }
        dispatch(setBankProvider(provider));
        navigate("/payment/send-money/same-currency/bank-recipient/recipient-info");
    };

    return (
        <OnboardingLayout title="Send money" showSkip={false}>
            <div className="max-w-2xl mx-auto md:px-3 mt-10 space-y-10 pb-24 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white rounded-xl p-6">
                    <div className="flex items-center pb-3 text-2xl font-normal leading-none">
                        <span className="mr-2 text-[#475467] md:!pt-5 text-normal font-thin">
                            {currencyPairCheck(currency)}
                        </span>
                        <div className="border-b md:mr-16">
                            <InputNumberFormat
                                value={formatted}
                                onChange={(e) => {
                                    if (e.target.value === "") return handleAmountClear();
                                    return handleAmountChange(e);
                                }}
                                locales={LOCALE}
                                maximumFractionDigits={2}
                                type="text"
                                inputMode="decimal"
                                className="!shadow-none font-thin text-3xl md:text-7xl outline-none w-full caret-[#6F00FF] placeholder:text-[#C4C6DE]"
                                placeholder="0"
                                aria-invalid={!!blockReason}
                                aria-describedby={blockReason ? "amount-error" : undefined}
                            />
                        </div>

                        <div className="relative min-w-[0px] flex justify-end mt-3">
                            <CurrencyToggle
                                className="w-full text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 xf"
                                fetchWallet={true}
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel={true}
                            />
                        </div>
                    </div>

                    <div className="flex ml-2 md:ml-6 justify-between mb-4 md:mb-8">
                        {(!isUSD || usdPricingInputsReady) &&
                            feeReady &&
                            minAmount > 0 && (
                                <p className="text-[14px] text-[#6F739B] font-[400]">
                                    Minimum amount is {currencyPairCheck(currency)}
                                    {formatMoney(minAmount)}
                                </p>
                            )}
                        <span className="text-[14px] text-[#6F739B] font-[400]">
                            Balance: {currencyPairCheck(currency)}
                            {formatMoney(balance)}
                        </span>
                    </div>

                    {blockReason && (
                        <div className="mt-2 text-sm text-red-600" role="alert" id="amount-error">
                            {blockReason}
                        </div>
                    )}

                    <div className="text-sm text-[#101828] space-y-2">
                        {isUSD && (
                            <div className="flex justify-between font-thin">
                                <span className="text-[#161827] text-[16px] font-[450]">
                                    Recipient location
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowRecipientLocationModal(true)}
                                    className="text-[#101828] inline-flex items-center gap-1"
                                    aria-haspopup="dialog"
                                >
                                    <span className="text-[#6F00FF] font-[450] text-[16px]">
                                        {recipientLocationLabel}
                                    </span>
                                    <ArrowRight2 size={20} />
                                </button>
                            </div>
                        )}

                        {isUSD && recipientLocation === "US" && (
                            <div className="flex justify-between font-thin">
                                <span className="text-[#161827] text-[16px] font-[450]">
                                    Payment method
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowTransferMethodModal(true)}
                                    className="text-[#101828] inline-flex items-center gap-1"
                                    aria-haspopup="dialog"
                                >
                                    <span className="text-[#6F00FF] font-[450] text-[16px]">
                                        {transferMethodLabel}
                                    </span>
                                    <ArrowRight2 size={20} />
                                </button>
                            </div>
                        )}

                        {hasMultipleProviders && (
                            <div className="flex justify-between font-thin">
                                <span className="text-[#161827] text-[16px] font-[450]">
                                    {provider ? (
                                        <>
                                            Sending via {provider.bankName || provider.bank || "—"}{" "}
                                            ({provider.accountNumber || "—"})
                                        </>
                                    ) : (
                                        "No bank account selected"
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowProviderModal(true)}
                                    className="text-[#101828] inline-flex items-center gap-1"
                                    aria-haspopup="dialog"
                                >
                                    <span className="text-[#6F00FF] font-[450] text-[16px]">
                                        {provider ? "Change bank" : "Select bank"}
                                    </span>
                                    <ArrowRight2 size={20} />
                                </button>
                            </div>
                        )}

                        <div className="flex justify-between text-[#161827] font-[350]  text-[16px]">
                            <span>Transfer fee</span>
                            <span>
                                {isUSD && !usdPricingInputsReady
                                    ? "—"
                                    : `${currencyPairCheck(currency)}${formatMoney(
                                          Number(fee) || 0
                                      )}${feeLoading ? " …" : ""}`}
                            </span>
                        </div>
                        <div className="flex justify-between text-[#161827]  font-[350] text-[16px]">
                            <span>Total to deduct</span>
                            <span>
                                {currencyPairCheck(currency)}
                                {formatMoney(total || 0)}
                            </span>
                        </div>
                    </div>
                    <div className="flex justify-between items-center pt-4 text-sm text-[#6B7280]"></div>
                </div>

                <div className="hidden md:flex justify-between items-center mt-6">
                    <div className="flex items-center gap-2 font-[350] text-[14px] text-[#161827]">
                        <Timer1 size="22" color="#6F739B" />
                        {currency === "USD" ? deliveryByRail : "Sent instantly"}
                    </div>

                    <div className="flex gap-5">
                        <button onClick={() => navigate(-1)} className="text-[#667085] font-medium">
                            Back
                        </button>
                        <button
                            onClick={onContinue}
                            disabled={!isAmountValid || showError}
                            title={needsProvider ? "Select a bank account" : ""}
                            className={`rounded-lg px-6 py-2 font-semibold text-white transition ${
                                isAmountValid
                                    ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                    : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                            }`}
                        >
                            Continue
                        </button>
                    </div>
                </div>

                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-4 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">
                                {currency === "USD" ? deliveryByRail : "Sent instantly"}
                            </span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="text-[#161827] font-medium w-[40%]"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!isAmountValid) return;
                                    onContinue();
                                }}
                                onTouchEnd={(e) => {
                                    if (!isAmountValid) return;
                                    e.preventDefault();
                                    onContinue();
                                }}
                                disabled={!isAmountValid || showError}
                                title={
                                    !isAmountValid
                                        ? needsProvider
                                            ? "Select a bank account"
                                            : tooLow
                                              ? `Minimum is ${currencyPairCheck(
                                                    currency
                                                )}${formatMoney(minAmount)}`
                                              : tooHigh
                                                ? `Maximum is ${currencyPairCheck(
                                                      currency
                                                  )}${formatMoney(maxAmount)}`
                                                : exceedDaily
                                                  ? `Exceeds daily limit. Remaining: ${formatMoney(
                                                        dailyRemaining
                                                    )} ${String(currency).toUpperCase()}`
                                                  : exceedMonthly
                                                    ? `Exceeds monthly limit. Remaining: ${formatMoney(
                                                          monthlyRemaining
                                                      )} ${String(currency).toUpperCase()}`
                                                    : insufficient
                                                      ? "Insufficient balance"
                                                      : ""
                                        : ""
                                }
                                className={`h-12 rounded-xl !px-5 font-semibold text-white transition w-[48%] ${
                                    isAmountValid
                                        ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                        : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                                }`}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <RecipientTypeModal
                open={showRecipientLocationModal}
                active={recipientLocation}
                title="Recipient bank location"
                options={[
                    {
                        id: "US",
                        title: "Recipient bank in the US",
                        desc: "Send using routing and account numbers to US-based banks",
                        icon: <Location />,
                    },
                    {
                        id: "NON_US",
                        title: "Recipient bank outside the US",
                        desc: "Send using IBAN to banks outside the US",
                        icon: <Map1 />,
                    },
                ]}
                onSelect={(id) => {
                    setRecipientLocation(id);
                    setPaymentRail((currentRail) => {
                        if (id === "NON_US") return "swift";
                        return recipientLocation === "NON_US" ? "" : currentRail;
                    });
                    setShowTransferMethodModal(false);
                    setShowRecipientLocationModal(false);
                }}
                onClose={() => setShowRecipientLocationModal(false)}
            />
            {recipientLocation === "US" && (
                <RecipientTypeModal
                    open={showTransferMethodModal}
                    active={paymentRail}
                    title="Payment method"
                    options={[
                        {
                            id: "ach",
                            title: "ACH",
                            desc: "Best for everyday bank transfers. Lower cost.",
                            icon: <ACHIcon />,
                        },
                        {
                            id: "rtp",
                            title: "RTP",
                            desc: "Best for faster transfers when supported.",
                            icon: <RTPIcon />,
                        },
                        {
                            id: "wire",
                            title: "Fedwire Transfer",
                            desc: "Best for large or urgent payments.",
                            icon: <WireIcon />,
                        },
                    ]}
                    onSelect={(id) => {
                        setPaymentRail(id);
                        setShowTransferMethodModal(false);
                    }}
                    onClose={() => setShowTransferMethodModal(false)}
                />
            )}

            <SelectBankProviderModal
                isOpen={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onSelect={(p) => setProviderState(p)}
                selectedId={provider?._id || provider?.accountNumber}
                providers={providers}
                currency={currency}
            />
        </OnboardingLayout>
    );
};

export default SameCurrencyOtherUsers;
